import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../../store/eventsSlice'
import { formatEventDateTime } from '../../utils/datetime'

// Matches the fixed ink colour `CoaPdfGenerator` draws body text in (title, event title, names,
// organisation, meta values and labels). Only the frame and dividers use the event's own `primary`
// colour — see CoaPdfGenerator/CoaPdfContent.
const COA_INK = '#1F2A37'

// Mirrors `CoaI18n.roleBackgroundHex` on the backend: the pale end colour of the page's diagonal
// background gradient (fading in from white), distinguishing recipients by role at a glance. This
// preview's sample role is always "Speaker" (see `ep_preview_coa_sample_role`), so it always shows
// the Speaker tint.
const COA_SPEAKER_BACKGROUND = '#FDECEC'

// Mirrors `CoaI18n.roleAccentBackgroundHex`: a more saturated shade of the same role hue, used only
// for the faint decorative diamonds layered into the top-right corner (never for solid fills or
// text) — see `ACCENT_SHAPES` below.
const COA_SPEAKER_ACCENT = '#F87171'

// Mirrors `CoaPdfGenerator`'s `ACCENT_SHAPE_RADII`/`ACCENT_SHAPE_ALPHAS`/corner anchor: a few
// nested, low-opacity diamonds forming a simple vector "sparkle" pattern in the page's top-right
// corner. Sizes are expressed as a percentage of the frame's own width (not height) so each shape
// renders as a true square before rotation, regardless of the page's portrait aspect ratio.
const ACCENT_SHAPES = [
  { widthPercent: 54.6, opacity: 0.08 },
  { widthPercent: 39.2, opacity: 0.13 },
  { widthPercent: 24.9, opacity: 0.19 },
]

/** Props for {@link CertificateView}. */
export interface CertificateViewProps {
  event: AppEvent
}

/**
 * Read-only preview of the Certificate of Attendance a participant receives after the event ends
 * (see `ep_field_auto_reminder` for the earlier reminder-email preview, and EP-13 for the
 * certificate feature itself).
 *
 * <p>The certificate is drawn entirely in code by {@code CoaPdfGenerator} (no background template
 * image) — this preview mirrors that same top-to-bottom structure (logo, title, family/given name
 * values, organisation, event title, role/date/organizer rows) with plain CSS instead of a
 * flattened background image, so it stays visually faithful without re-implementing a fixed
 * template's artwork. Only the frame and dividers are tinted with the event's own {@code primary}
 * colour, matching the generated PDF; the title, event title, names, organisation, meta values and
 * meta labels all share the same fixed ink colour. The page also carries a diagonal background
 * gradient plus a few decorative corner diamonds matching the recipient's role (here, always the
 * "Speaker" sample tint/accent) — a simple vector pattern, not a flat tone.
 */
export function CertificateView({ event }: CertificateViewProps) {
  const { t, i18n } = useTranslation()
  const primary = event.branding.primary

  const dateLine = event.startDateTime
    ? formatEventDateTime(event.startDateTime, event.timezone, i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  return (
    <div className="bg-pax-light-purple/30 flex min-h-screen flex-col items-center gap-4 px-6 py-12">
      <div
        data-testid="preview-coa-frame"
        className="relative flex w-full max-w-md flex-col overflow-hidden p-3 shadow-lg"
        style={{
          aspectRatio: '595.28 / 841.89',
          border: `1.5px solid ${primary}`,
          background: `linear-gradient(135deg, #FFFFFF 0%, ${COA_SPEAKER_BACKGROUND} 85%)`,
        }}
      >
        {ACCENT_SHAPES.map((shape) => (
          <div
            key={shape.widthPercent}
            aria-hidden="true"
            className="pointer-events-none absolute rounded-sm"
            style={{
              left: '86%',
              top: '8%',
              width: `${shape.widthPercent}%`,
              aspectRatio: '1 / 1',
              backgroundColor: COA_SPEAKER_ACCENT,
              opacity: shape.opacity,
              transform: 'translate(-50%, -50%) rotate(45deg)',
            }}
          />
        ))}
        <div
          className="relative z-10 flex h-full w-full flex-col items-center gap-3 p-5 text-center"
          style={{ border: `0.75px solid ${primary}` }}
        >
          {event.branding.logo && (
            <img
              src={event.branding.logo.dataUrl}
              alt=""
              className="max-h-20 max-w-[65%] object-contain"
            />
          )}

          <h2 className="text-base font-semibold" style={{ color: COA_INK }}>
            {t('ep_preview_coa_title')}
          </h2>
          <Divider color={primary} />

          <div className="flex w-full items-start justify-center gap-6">
            <NameField value={t('ep_preview_coa_sample_family_name')} />
            <NameField value={t('ep_preview_coa_sample_given_name')} />
          </div>

          <p className="text-[11px]" style={{ color: COA_INK }}>
            {t('ep_preview_coa_sample_organization')}
          </p>

          <p className="text-[11px]" style={{ color: COA_INK }}>
            {t('ep_preview_coa_attended')}
          </p>

          <p className="max-w-[85%] text-sm font-semibold" style={{ color: COA_INK }}>
            {event.title}
          </p>

          <Divider color={primary} />

          <div className="flex w-full max-w-[85%] flex-col gap-1.5">
            <MetaRow
              label={t('ep_preview_coa_role_label')}
              value={t('ep_preview_coa_sample_role')}
            />
            {dateLine && <MetaRow label={t('ep_preview_coa_date_label')} value={dateLine} />}
            <MetaRow
              label={t('ep_preview_coa_organizer_label')}
              value={t('ep_preview_coa_sample_organizer')}
            />
          </div>
        </div>
      </div>
      <p className="text-pax-gray max-w-md text-center text-xs">{t('ep_preview_coa_note')}</p>
    </div>
  )
}

function Divider({ color }: { color: string }) {
  return <div className="h-px w-24" style={{ backgroundColor: color }} />
}

/** One underlined name value (no caption above it) — the "fill in the blank" look of a printed
 * certificate line, matching {@code CoaPdfGenerator}'s family-name / given-name column. */
function NameField({ value }: { value: string }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5">
      <span className="text-sm font-medium" style={{ color: COA_INK }}>
        {value}
      </span>
      <span className="h-px w-full opacity-40" style={{ backgroundColor: COA_INK }} />
    </div>
  )
}

/** One "label: value" meta row (role / date / organizer), matching {@code CoaPdfGenerator}'s
 * aligned label/value columns; both the label and value use the same fixed ink colour. */
function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 text-left text-[11px]">
      <span className="font-medium whitespace-nowrap" style={{ color: COA_INK }}>
        {label}:
      </span>
      <span style={{ color: COA_INK }}>{value}</span>
    </div>
  )
}
